import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';

type I18nText = Record<string, string>;
const LANGS = [
  { code: 'pt-BR', key: 'lang_pt' },
  { code: 'en', key: 'lang_en' },
  { code: 'es', key: 'lang_es' }
] as const;

export function LoyaltyPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [active, setActive] = useState(false);
  const [mode, setMode] = useState('per_visit');
  const [perVisit, setPerVisit] = useState('1');
  const [perCurrency, setPerCurrency] = useState('1');
  const [threshold, setThreshold] = useState('10');
  const [reward, setReward] = useState<I18nText>({ 'pt-BR': '', en: '', es: '' });
  const [saved, setSaved] = useState(false);

  const [code, setCode] = useState('');
  const [validateMsg, setValidateMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase.from('loyalty_programs').select('*').eq('restaurant_id', restaurant.id).maybeSingle();
    if (data) {
      setActive(data.is_active);
      setMode(data.accrual_mode);
      setPerVisit(String(data.points_per_visit));
      setPerCurrency(String(data.points_per_currency_unit));
      setThreshold(String(data.reward_threshold));
      setReward({ 'pt-BR': '', en: '', es: '', ...(data.reward_description as I18nText) });
    }
  }, [restaurant]);

  useEffect(() => { void load(); }, [load]);

  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!restaurant) return;
    setSaved(false);
    await supabase.from('loyalty_programs').upsert(
      {
        restaurant_id: restaurant.id,
        is_active: active,
        accrual_mode: mode,
        points_per_visit: Number(perVisit) || 0,
        points_per_currency_unit: Number(perCurrency) || 0,
        reward_threshold: Number(threshold) || 1,
        reward_description: reward
      },
      { onConflict: 'restaurant_id' }
    );
    setSaved(true);
  }

  async function validate() {
    setValidateMsg(null);
    const { data, error } = await supabase.rpc('validate_redemption', { p_code: code.trim() });
    if (error) return setValidateMsg(error.message);
    const r = data as { status: string; reward: string };
    setValidateMsg(`${r.status} · ${r.reward}`);
    setCode('');
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page">
      <h1 className="page__title">{t('loyalty.title')}</h1>
      <p className="muted-note">{t('loyalty.hint')}</p>

      <form onSubmit={save} className="form">
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>{t('loyalty.active')}</span>
        </label>

        <div className="form-row">
          <label className="field">
            <span>{t('loyalty.mode')}</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="per_visit">{t('loyalty.per_visit')}</option>
              <option value="per_amount">{t('loyalty.per_amount')}</option>
            </select>
          </label>
          {mode === 'per_visit' ? (
            <label className="field">
              <span>{t('loyalty.points_per_visit')}</span>
              <input type="number" min={0} value={perVisit} onChange={(e) => setPerVisit(e.target.value)} />
            </label>
          ) : (
            <label className="field">
              <span>{t('loyalty.points_per_currency')}</span>
              <input type="number" min={0} step="0.1" value={perCurrency} onChange={(e) => setPerCurrency(e.target.value)} />
            </label>
          )}
          <label className="field">
            <span>{t('loyalty.threshold')}</span>
            <input type="number" min={1} value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </label>
        </div>

        <div className="i18n-fields">
          {LANGS.map((l) => (
            <label key={l.code} className="field">
              <span>{t('loyalty.reward')} · {t(`menu.${l.key}`)}</span>
              <input value={reward[l.code] ?? ''} onChange={(e) => setReward({ ...reward, [l.code]: e.target.value })} required={l.code === 'pt-BR'} />
            </label>
          ))}
        </div>

        <div className="form-row">
          <button type="submit" className="btn btn--primary">{t('loyalty.save')}</button>
          {saved && <span className="form-info">{t('loyalty.saved')}</span>}
        </div>
      </form>

      <h2 className="section-title">{t('loyalty.validate_title')}</h2>
      <div className="form-row">
        <input placeholder={t('loyalty.code')} value={code} onChange={(e) => setCode(e.target.value)} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-creme)' }} />
        <button type="button" className="btn btn--ghost" onClick={() => void validate()}>{t('loyalty.validate')}</button>
      </div>
      {validateMsg && <p className="form-info">{validateMsg}</p>}
    </div>
  );
}
